import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getRecentDashboards, saveRecentDashboard } from '../utils/recentDashboards';
import { prefetchCrewActivities } from '../utils/crewTimesheetActivitiesCache';
import { useAuthFetch } from '../hooks/useAuthFetch';
import { RecentDashboardActivityLinks } from './CrewTimesheetRecentButton';
import ReportPickerModal, { applyReportSelection } from './ReportPickerModal';
import './RecentDashboards.css';

export function formatRecentDate(timestamp) {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function RecentIcon({ active, accent = '#DC2626' }) {
  const stroke = active ? accent : '#374151';

  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M10 2L3 7V17C3 17.5523 3.44772 18 4 18H7V12H13V18H16C16.5523 18 17 17.5523 17 17V7L10 2Z"
        stroke={stroke}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10 2V8H17"
        stroke={stroke}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function useRecentDashboards(onAfterNavigate) {
  const navigate = useNavigate();
  const { getAuthConfig, isReady } = useAuthFetch();
  const [recentDashboards, setRecentDashboards] = useState([]);

  useEffect(() => {
    const loadRecentDashboards = () => {
      setRecentDashboards(getRecentDashboards());
    };

    loadRecentDashboards();
    window.addEventListener('projectDetailsChanged', loadRecentDashboards);
    window.addEventListener('recentDashboardsUpdated', loadRecentDashboards);

    return () => {
      window.removeEventListener('projectDetailsChanged', loadRecentDashboards);
      window.removeEventListener('recentDashboardsUpdated', loadRecentDashboards);
    };
  }, []);

  useEffect(() => {
    if (!isReady) return;
    prefetchCrewActivities(
      recentDashboards.map((dashboard) => dashboard.projectId),
      getAuthConfig
    );
  }, [getAuthConfig, isReady, recentDashboards]);

  const [reportDashboard, setReportDashboard] = useState(null);

  const openRecent = (dashboard, report) => {
    saveRecentDashboard({
      projectId: dashboard.projectId,
      projectName: dashboard.projectName,
      opportunityNo: dashboard.opportunityNo,
      id_project: dashboard.opportunityId,
      Id: dashboard.opportunityId,
      department: dashboard.department,
    });

    setRecentDashboards(getRecentDashboards());

    localStorage.setItem('selectedDepartment', dashboard.department || 'Engineered Fill');
    localStorage.setItem('isProjectSelected', 'true');
    localStorage.setItem('selectedRowId', dashboard.opportunityId || '');
    localStorage.setItem('scrollToSelectedRow', 'true');

    if (dashboard.opportunityId) {
      window.dispatchEvent(
        new CustomEvent('loadRecentDashboard', {
          detail: {
            opportunityId: dashboard.opportunityId,
            opportunityNo: dashboard.opportunityNo,
          },
        })
      );
    }

    window.scrollTo(0, 0);
    if (report && dashboard.opportunityId) {
      const params = new URLSearchParams();
      applyReportSelection(params, report);
      params.set('opportunityId', dashboard.opportunityId);
      navigate({ pathname: '/dashboard', search: params.toString() });
    } else {
      navigate('/dashboard');
    }
    onAfterNavigate?.();
  };

  return {
    recentDashboards,
    handleDashboardClick: (dashboard) => openRecent(dashboard, false),
    // Report opens a popup first; the chosen report opens after OK.
    handleReportClick: (dashboard) => setReportDashboard(dashboard),
    reportPickerModal: (
      <ReportPickerModal
        open={Boolean(reportDashboard)}
        projectName={reportDashboard?.projectName}
        onClose={() => setReportDashboard(null)}
        onConfirm={(selection) => {
          const target = reportDashboard;
          setReportDashboard(null);
          if (target) openRecent(target, selection);
        }}
      />
    ),
  };
}

function RecentReportButton({ onClick }) {
  return (
    <button type="button" className="recent-dashboards-report" onClick={onClick} title="Open daily report">
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M8 3.5h6.2L19 8.2V20a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 7 20V5A1.5 1.5 0 0 1 8.5 3.5H8z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
        <path d="M14 3.8V8.2H18.4M9.2 12.2h5.6M9.2 15.6h5.6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      Report
    </button>
  );
}

export function RecentDashboardsSidebarSection({ onItemSelect }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { recentDashboards, handleDashboardClick, handleReportClick, reportPickerModal } =
    useRecentDashboards(onItemSelect);

  return (
    <>
      {reportPickerModal}
      <div className={`menu-item-wrapper ${isExpanded ? 'active' : ''}`}>
        <div className={`indicator ${isExpanded ? 'active' : ''}`} />
        <div
          className="menu-item"
          onClick={() => setIsExpanded(!isExpanded)}
          style={{ cursor: 'pointer' }}
        >
          <RecentIcon active={isExpanded} />
          <span className="menu-text">Recent</span>
          <svg
            className={`dropdown-icon ${isExpanded ? 'rotate-180' : ''}`}
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M5 7.5L10 12.5L15 7.5"
              stroke={isExpanded ? '#DC2626' : '#374151'}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>
      {isExpanded && (
        <div className="recent-dashboards-sidebar-list">
          {recentDashboards.length === 0 ? (
            <div className="recent-dashboards-sidebar-empty">
              <p>No recent dashboards</p>
              <span>Open a project to see it here</span>
            </div>
          ) : (
            recentDashboards.map((dashboard, index) => (
              <div
                key={`${dashboard.projectId}-${dashboard.opportunityNo}-${index}`}
                className="recent-dashboards-sidebar-item"
              >
                <div className="recent-dashboards-sidebar-item-body">
                  <div className="recent-dashboards-sidebar-item-main">
                    <button
                      type="button"
                      className="recent-dashboards-sidebar-item-hit"
                      onClick={() => handleDashboardClick(dashboard)}
                    >
                      <div className="recent-dashboards-sidebar-item-content">
                        <div className="recent-dashboards-sidebar-item-name">{dashboard.projectName}</div>
                        <div className="recent-dashboards-sidebar-item-meta">
                          <span>{dashboard.projectId}</span>
                          {dashboard.department && <span>{dashboard.department}</span>}
                        </div>
                      </div>
                    </button>
                    <div className="recent-dashboards-item-aside">
                      <span className="recent-dashboards-sidebar-item-time">
                        {formatRecentDate(dashboard.timestamp)}
                      </span>
                      <RecentReportButton onClick={() => handleReportClick(dashboard)} />
                    </div>
                  </div>
                  <RecentDashboardActivityLinks
                    dashboard={dashboard}
                    onFlowStarted={onItemSelect}
                  />
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </>
  );
}

function RecentDashboards() {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { recentDashboards, handleDashboardClick, handleReportClick, reportPickerModal } = useRecentDashboards(() => setIsOpen(false));

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (event.target.closest?.('[data-crew-activity-menu]')) return;
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="recent-dashboards-container" ref={dropdownRef}>
      {reportPickerModal}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`recent-dashboards-button ${isOpen ? 'active' : ''}`}
        title="Recent"
      >
        <RecentIcon active={isOpen} accent="#2563eb" />
        <span className="recent-dashboards-label">Recent</span>
      </button>

      {isOpen && (
        <div className="recent-dashboards-dropdown">
          {recentDashboards.length === 0 ? (
            <div className="recent-dashboards-empty">
              <p>No recent dashboards</p>
              <span className="recent-dashboards-empty-hint">Open a project to see it here</span>
            </div>
          ) : (
            <>
              <div className="recent-dashboards-header">
                <span>Recent Dashboards</span>
                <span className="recent-dashboards-header-count">{recentDashboards.length}</span>
              </div>
              <div className="recent-dashboards-list">
                {recentDashboards.map((dashboard, index) => (
                  <div
                    key={`${dashboard.projectId}-${dashboard.opportunityNo}-${index}`}
                    className="recent-dashboards-item"
                  >
                    <div className="recent-dashboards-item-body">
                      <div className="recent-dashboards-item-main">
                        <button
                          type="button"
                          className="recent-dashboards-item-hit"
                          onClick={() => handleDashboardClick(dashboard)}
                        >
                          <div className="recent-dashboards-item-content">
                            <div className="recent-dashboards-item-name">{dashboard.projectName}</div>
                            <div className="recent-dashboards-item-meta">
                              <span className="recent-dashboards-item-project">{dashboard.projectId}</span>
                              {dashboard.department && (
                                <span className="recent-dashboards-item-department">{dashboard.department}</span>
                              )}
                            </div>
                          </div>
                        </button>
                        <div className="recent-dashboards-item-aside">
                          <div className="recent-dashboards-item-time">
                            {formatRecentDate(dashboard.timestamp)}
                          </div>
                          <RecentReportButton onClick={() => handleReportClick(dashboard)} />
                        </div>
                      </div>
                      <RecentDashboardActivityLinks
                        dashboard={dashboard}
                        onFlowStarted={() => setIsOpen(false)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default RecentDashboards;
